import React, {useEffect, useState} from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Grid,
  InputLabel,
  FormHelperText,
  Snackbar
} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import MDBox from "components/MDBox";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDButton from "components/MDButton";
import properties from "constants/ApplicationConstants";
import {POST} from "APIclient";
import {GET} from "APIclient";
import {useNavigate} from "react-router-dom";

const OpenKRAWindow = ({
  title = "Performance Appraisal",
  performance = {
    evalution_start_date: dayjs(),
    evalution_end_date: dayjs()
  },
  reportingNotSet = []
}) => {
  const [evaStartDate, setEvaStartDate] = useState(dayjs(performance.evalution_start_date));
  const [evaEndDate, setEvaEndDate] = useState(dayjs(performance.evalution_end_date));

  const [formEditDeadline, setFormEditDeadline] = useState(null);
  const [empKraStart, setEmpKraStart] = useState(null);
  const [empKraEnd, setEmpKraEnd] = useState(null);
  const [mgrKraStart, setMgrKraStart] = useState(null);
  const [mgrKraEnd, setMgrKraEnd] = useState(null);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackColor, setSnackColor] = useState("success");
  const [snackMessage, setSnackMessage] = useState("");
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const navigate = useNavigate();

  const handleClose = () => {
    setSnackOpen(false);
  };

  const [errors, setErrors] = useState({});

  const handleStartDateChange = newValue => {
    if (!newValue || !newValue.isValid()) return;

    setEvaStartDate(newValue);

    const evalEndDate = newValue.add(6, "month").subtract(1, "day");
    setEvaEndDate(adjustToWeekday(evalEndDate));

    const formDeadline = adjustToWeekday(newValue.endOf("month"));
    setFormEditDeadline(formDeadline);
    setErrors(prev => ({...prev, form_edit_deadline: null}));

    const empStart = evalEndDate.date(15);
    const empEnd = adjustToWeekday(empStart.add(7, "day"));
    setEmpKraStart(empStart);
    setEmpKraEnd(empEnd);
    setErrors(prev => ({...prev, employee_kra_start_date: null, employee_kra_end_date: null}));

    const mgrEnd = adjustToWeekday(empStart.endOf("month"));
    setMgrKraStart(empStart);
    setMgrKraEnd(mgrEnd);
    setErrors(prev => ({...prev, manager_kra_start_date: null, manager_kra_end_date: null}));
  };

  const adjustToWeekday = date => {
    const day = date.day();
    if (day === 6) return date.subtract(1, "day");
    if (day === 0) return date.subtract(2, "day");
    return date;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    const newErrors = {};

    if (!formEditDeadline) newErrors.form_edit_deadline = "This field is required";
    if (!empKraStart) newErrors.employee_kra_start_date = "Start date is required";
    if (!empKraEnd) newErrors.employee_kra_end_date = "End date is required";
    if (!mgrKraStart) newErrors.manager_kra_start_date = "Start date is required";
    if (!mgrKraEnd) newErrors.manager_kra_end_date = "End date is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const payload = {
      evalutionStartDate: evaStartDate.format("YYYY-MM-DD"),
      evalutionEndDate: evaEndDate.format("YYYY-MM-DD"),
      formEditDeadline: formEditDeadline.format("YYYY-MM-DD"),
      employeeKraStartDate: empKraStart.format("YYYY-MM-DD"),
      employeeKraEndDate: empKraEnd.format("YYYY-MM-DD"),
      managerKraStartDate: mgrKraStart.format("YYYY-MM-DD"),
      managerKraEndDate: mgrKraEnd.format("YYYY-MM-DD")
    };

    try {
      const url = "admin/appraisal-cycle/add";
      const response = await POST(url, payload);

      if (response?.status === 200) {
        const resData = response?.data;
        setSnackMessage(resData?.message);
        setSnackColor("success");
        setSnackOpen(true);
      }
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to create appraisal cycle";
      setSnackMessage(message);
      setSnackColor("error");
      setSnackOpen(true);
    }
  };

  useEffect(() => {
    const fetchLastKraCycle = async () => {
      try {
        const url = "admin/appraisal-cycle/last";
        const response = await GET(url);
        if (response?.status === 200) {
          const lastCycle = response?.data?.data;
          setEvaStartDate(dayjs(lastCycle.evalutionStartDate));
          setEvaEndDate(dayjs(lastCycle.evalutionEndDate));
        }
      } catch (error) {
        const message =
          error?.response?.data?.message || "Failed to fetch previous appraisal cycle";
        setSnackMessage(message);
        setSnackColor("error");
        setSnackOpen(true);
      }
    };
    fetchLastKraCycle();
  }, []);

  useEffect(() => {
    if (
      formEditDeadline &&
      empKraStart &&
      empKraEnd &&
      mgrKraStart &&
      mgrKraEnd &&
      evaStartDate &&
      evaEndDate
    ) {
      setIsButtonDisabled(false);
    } else {
      setIsButtonDisabled(true);
    }
  }, [formEditDeadline, empKraStart, empKraEnd, mgrKraStart, mgrKraEnd, evaStartDate, evaEndDate]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{flexGrow: 1, p: 2}}>
            {reportingNotSet.length > 0 && (
              <Alert severity="error" sx={{mb: 3}}>
                Reporting Manager not set for {reportingNotSet.length} employees.
              </Alert>
            )}

            <Paper elevation={3}>
              <Box component="form" onSubmit={handleSubmit} noValidate>
                <Box sx={{background: properties.colors.bgcolorbluethem, color: "white", p: 2}}>
                  <Typography
                    variant="h6"
                    fontSize={15}
                    sx={{
                      color: properties.colors.White,
                      fontFamily: properties.fontFamily
                    }}
                  >
                    Performance Evaluation
                  </Typography>
                </Box>

                <Box sx={{p: 2}}>
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <InputLabel
                        sx={{fontWeight: "bold", fontSize: "13", fontFamily: properties.fontFamily}}
                      >
                        Evaluation Date <span style={{color: "red"}}>*</span>
                      </InputLabel>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        value={evaStartDate}
                        format="DD-MM-YYYY"
                        slotProps={{textField: {fullWidth: true}}}
                        onChange={handleStartDateChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        value={evaEndDate}
                        format="DD-MM-YYYY"
                        slotProps={{textField: {fullWidth: true}}}
                        onChange={newValue => setEvaEndDate(newValue)}
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Box
                  sx={{background: properties.colors.bgcolorbluethem, color: "white", p: 2, mt: 2}}
                >
                  <Typography
                    variant="h6"
                    fontSize={15}
                    sx={{
                      color: properties.colors.White,
                      fontFamily: properties.fontFamily
                    }}
                  >
                    KRA Details
                  </Typography>
                </Box>

                <Box sx={{p: 3}}>
                  <Grid container spacing={3} sx={{mb: 3}} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <InputLabel
                        sx={{
                          fontWeight: "bold",
                          fontFamily: properties.fontFamily,
                          lineHeight: "1.5",
                          paddingBottom: "4px"
                        }}
                      >
                        KRA Form Edit Deadline <span style={{color: "red"}}>*</span>
                      </InputLabel>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        value={formEditDeadline}
                        onChange={newValue => {
                          setFormEditDeadline(newValue);
                          setErrors(prev => ({...prev, form_edit_deadline: null}));
                        }}
                        format="DD-MM-YYYY"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.form_edit_deadline,
                            helperText: errors.form_edit_deadline
                          }
                        }}
                      />
                      <FormHelperText sx={{color: "black", fontWeight: "500"}}>
                        (For Manager)
                      </FormHelperText>
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} sx={{mb: 3}} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <InputLabel
                        sx={{
                          fontWeight: "bold",
                          fontFamily: properties.fontFamily,
                          lineHeight: "1.5",
                          paddingBottom: "4px"
                        }}
                      >
                        KRA Window For Employee <span style={{color: "red"}}>*</span>
                      </InputLabel>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Open From"
                        value={empKraStart}
                        onChange={newValue => {
                          setEmpKraStart(newValue);
                          setErrors(prev => ({...prev, employee_kra_start_date: null}));
                        }}
                        format="DD-MM-YYYY"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.employee_kra_start_date,
                            helperText: errors.employee_kra_start_date
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Close On"
                        value={empKraEnd}
                        onChange={newValue => {
                          setEmpKraEnd(newValue);
                          setErrors(prev => ({...prev, employee_kra_end_date: null}));
                        }}
                        format="DD-MM-YYYY"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.employee_kra_end_date,
                            helperText: errors.employee_kra_end_date
                          }
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <InputLabel
                        sx={{
                          fontWeight: "bold",
                          fontFamily: properties.fontFamily,
                          lineHeight: "1.5",
                          paddingBottom: "4px"
                        }}
                      >
                        KRA Window For Manager <span style={{color: "red"}}>*</span>
                      </InputLabel>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Open From"
                        value={mgrKraStart}
                        onChange={newValue => {
                          setMgrKraStart(newValue);
                          setErrors(prev => ({...prev, manager_kra_start_date: null}));
                        }}
                        format="DD-MM-YYYY"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.manager_kra_start_date,
                            helperText: errors.manager_kra_start_date
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Close On"
                        value={mgrKraEnd}
                        onChange={newValue => {
                          setMgrKraEnd(newValue);
                          setErrors(prev => ({...prev, manager_kra_end_date: null}));
                        }}
                        format="DD-MM-YYYY"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.manager_kra_end_date,
                            helperText: errors.manager_kra_end_date
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Box sx={{p: 3, textAlign: "right", gap: 1, bgcolor: "grey.100"}}>
                  <MDButton
                    variant="gradient"
                    color="warning"
                    sx={{width: "15ch", mr: 2, fontFamily: properties.fontFamily}}
                    onClick={() => navigate(-1)}
                  >
                    Back
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="#fff"
                    sx={{
                      marginRight: "3px",
                      width: "20ch",
                      background: properties.colors.bgcolorbluethem,
                      fontFamily: properties.fontFamily
                    }}
                    onClick={() => handleSubmit(new Event("submit"))}
                    disabled={isButtonDisabled}
                  >
                    Save
                  </MDButton>
                </Box>
              </Box>
            </Paper>
          </Box>
        </LocalizationProvider>
      </MDBox>
      <Snackbar
        open={snackOpen}
        anchorOrigin={{vertical: "top", horizontal: "right"}}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={snackColor === "success" ? "success" : "error"}
          variant="filled"
          sx={{width: "100%", color: "#fff"}}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default OpenKRAWindow;
